import { action, computed, makeObservable, observable, override } from 'mobx'
import { combineLatest, distinctUntilChanged, Subscription } from 'rxjs'
import { func, validationContext } from 'validx'
import ModalViewModel from 'infra/ModalViewModel'
import { ConsultationIncompleteReason } from 'jobs/consultations/Consultation'
import { tapAction, toStream } from 'utils/mobx-util'

export default class IncompleteConsultationCompletionDialogViewModel extends ModalViewModel {
  /**
   * Subscription to stream of changes to observables.
   */
  private subscription = new Subscription()

  /**
   * The currently selected reason.
   */
  @observable
  reason?: ConsultationIncompleteReason = undefined

  /**
   * The custom reason the consultation was not completed.
   */
  @observable
  otherText = ''

  /**
   * Validation rules for the incomplete consultation completion dialog form.
   */
  validation = validationContext(this, {
    reason: [
      func(() => this.reason !== undefined, 'You must select a reason.'),
    ],
    otherText: [
      func(
        () =>
          this.reason !== ConsultationIncompleteReason.Other ||
          this.otherText.trim().length > 0,
        'If you select "Other", you must provide your own reason.',
      ),
    ],
  })

  /**
   * Stream listening for changes to the reason and otherText observables.
   */
  private reasonChanges$ = combineLatest([
    toStream(() => this.reason, true),
    toStream(() => this.otherText, true),
  ]).pipe(
    distinctUntilChanged(),
    tapAction(() => {
      this.validation.reset().validate()
    }),
  )

  constructor(
    private requestCloseWithIncompleteConsultation: (
      consultationIncompleteReason: ConsultationIncompleteReason,
      consultationIncompleteOtherReason?: string,
    ) => void,
  ) {
    super()
    makeObservable(this)
  }

  /**
   * Activates the view model.
   */
  activate() {
    this.subscription = this.reasonChanges$.subscribe()
  }

  /**
   * Deactivates the view model.
   */
  deactivate() {
    this.subscription.unsubscribe()
    this.reset()
  }

  /**
   * Whether the reason form is valid.
   */
  @computed
  get isValid() {
    return this.validation.isValid
  }

  /**
   * Sets the reason.
   */
  @action.bound
  setReason(reason?: ConsultationIncompleteReason) {
    this.reason = reason
  }

  /**
   * Sets the custom reason.
   */
  @action.bound
  setOtherText(otherText: string) {
    this.otherText = otherText
  }

  /**
   * Resets the reason form.
   */
  @action.bound
  reset() {
    this.reason = undefined
    this.otherText = ''
  }

  /**
   * Submits the reason and completes the job.
   */
  submit() {
    if (!this.isValid || !this.reason) {
      return
    }

    this.requestCloseWithIncompleteConsultation(this.reason, this.otherText)
    this.close()
  }

  /**
   * Activates the view model and shows the dialog.
   */
  @override
  show() {
    this.activate()
    super.show()
  }

  /**
   * Closes the dialog and deactivates the view model.
   */
  @override
  close() {
    super.close()
    this.deactivate()
  }
}
